import { createFileRoute } from '@tanstack/react-router';
import { lazy, useContext, useState, Suspense } from 'react';
import { useLocale, useTranslations } from 'use-intl';
import { useQuery } from '@tanstack/react-query';
import { AppContext } from '@/app/appContext';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderBackButton,
  HeaderButton,
  HeaderSidebarTrigger,
} from '@/app/ui/components/header';
import {
  Sidenav,
  SidenavButton,
  SidenavEmpty,
  SidenavHeader,
  SidenavHeading,
  SidenavItem,
  SidenavList,
} from '@/app/ui/components/sidenav';
import { HugeiconsIcon } from '@hugeicons/react';
import { AiBrain02Icon, Download01Icon, PlusSignIcon } from '@hugeicons/core-free-icons';
import {
  useKnowledgeBaseDocuments,
  useOrganizationKnowledgeBases,
} from '@/app/hooks/queries/useInternalKnowledgeBases';
import { getProxyInternalKnowledgeBaseIndexedDocumentsStatus } from '@/app/services/internalKnowledgeBasesService';
import { Document } from '@/app/types/models';
import { queryKeys } from '@/app/utils/constants';
import { Badge, BadgeDot } from '@/app/ui/components/badge';
import { getLocalizedDateDistance } from '@/app/utils/dates';
import { Card } from '@/app/ui/components/card';
import { Spinner } from '@/app/ui/components/spinner';
import { MainScrollArea } from '@/app/ui/components/main';
import { KbDocumentsUploadDialog } from '@/app/components/features/organizations/knowledgeBase/KbDocumentsUploadDialog';
import { Button } from '@/app/ui/components/button';
import { NotFound } from '@/app/ui/components/not-found';
import { downloadFileFromUrl } from '@/app/utils/helpers';

const InlineDocumentPreview = lazy(
  () => import('@/app/components/shared/Preview/InlineDocumentPreview'),
);

export const Route = createFileRoute(
  '/_authed/(organization)/_layout/knowledge-base/internal/$knowledgeBaseId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { knowledgeBaseId } = Route.useParams();
  const locale = useLocale();
  const { userMeta } = useContext(AppContext);
  const organizationId = userMeta.organizationId ?? '';
  const t = useTranslations('components.internalKnowledgeBase');
  const tPage = useTranslations('pages.knowledgeBase');
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);

  const { data: documents, isLoading } = useKnowledgeBaseDocuments(knowledgeBaseId);
  const { data: knowledgeBases, isPending: isLoadingKnowledgeBases } =
    useOrganizationKnowledgeBases(organizationId);
  const knowledgeBase = knowledgeBases?.find((kb) => kb.id === knowledgeBaseId);

  const { data: indexingStatus } = useQuery({
    queryKey: [queryKeys.INDEXING_STATUS, 'internalKnowledgeBase', knowledgeBaseId],
    queryFn: async () => {
      const response = await getProxyInternalKnowledgeBaseIndexedDocumentsStatus(knowledgeBaseId);
      return response?.data ?? null;
    },
    enabled: !!knowledgeBaseId,
    refetchInterval: (query) => {
      if (query.state.data?.indexedPercentage === 100) return false;
      return 5000;
    },
  });

  // auto-select first document if none selected
  const effectiveDocumentId = selectedDocumentId ?? documents?.[0]?.id;
  const selectedDocument = documents?.find((doc) => doc.id === effectiveDocumentId);

  const handleDownload = () => {
    if (!selectedDocument) return;
    downloadFileFromUrl(selectedDocument.sasUri, selectedDocument.name);
  };

  if (isLoadingKnowledgeBases) return <Spinner kind="inset" />;

  if (!knowledgeBase) return <NotFound />;

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink to={'/knowledge-base'}>
                <HugeiconsIcon icon={AiBrain02Icon} />
                {tPage('breadcrumbs.knowledgeBase')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator
              data-hide={selectedDocument ? '' : undefined}
              className="data-hide:max-md:hidden"
            />
            <BreadcrumbItem
              data-hide={selectedDocument ? '' : undefined}
              className="data-hide:max-md:hidden"
            >
              <BreadcrumbPage>{knowledgeBase.name}</BreadcrumbPage>
            </BreadcrumbItem>
            {selectedDocument && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{selectedDocument.name}</BreadcrumbPage>
                </BreadcrumbItem>
              </>
            )}
          </BreadcrumbList>
        </Breadcrumb>
        {selectedDocument && (
          <HeaderButton onClick={handleDownload} className="md:inline-flex" aria-label="Download">
            <HugeiconsIcon icon={Download01Icon} />
          </HeaderButton>
        )}
      </Header>
      <MainScrollArea viewportClassName="max-lg:flex-col flex grow">
        <Sidenav className="border-r">
          <SidenavHeader>
            <SidenavHeading>
              {tPage('details.numberOfVersions')}{' '}
              <strong className="text-foreground">{documents?.length ?? 0}</strong>
            </SidenavHeading>
            <Button
              kind="icon"
              variant="ghost"
              onClick={() => setIsUploadDialogOpen(true)}
              aria-label="Upload documents"
            >
              <HugeiconsIcon icon={PlusSignIcon} />
            </Button>
          </SidenavHeader>
          <SidenavList>
            {isLoading ? (
              <Spinner kind="inset" />
            ) : !documents || documents.length === 0 ? (
              <SidenavEmpty>{t('messages.noDocs')}</SidenavEmpty>
            ) : (
              documents.map((doc: Document) => (
                <SidenavItem key={doc.id}>
                  <SidenavButton
                    active={effectiveDocumentId === doc.id}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedDocumentId(doc.id);
                    }}
                  >
                    <span className="lg:line-clamp-1">{doc.name}</span>
                  </SidenavButton>
                </SidenavItem>
              ))
            )}
          </SidenavList>
        </Sidenav>
        <div className="grow flex flex-col md:bg-surface-2 min-w-0">
          {selectedDocument ? (
            <>
              <ul className="flex gap-2 p-4 scrollbar-hidden overflow-x-auto text-sm whitespace-nowrap pb-2">
                <Badge render={<li></li>}>{selectedDocument.sizeInMegaBytes.toFixed(2)} MB</Badge>
                <Badge render={<li></li>}>
                  <BadgeDot
                    variant={indexingStatus?.indexedPercentage === 100 ? 'success' : 'warning'}
                  />
                  {indexingStatus?.indexedPercentage === 100
                    ? tPage('details.indexed')
                    : tPage('details.indexingPending')}
                </Badge>
                <Badge render={<li></li>}>
                  {' '}
                  {tPage('details.created')}{' '}
                  {selectedDocument.createdAt &&
                    getLocalizedDateDistance(selectedDocument.createdAt, locale)}
                </Badge>
              </ul>
              <div className="p-4 grow flex flex-col min-h-0">
                <div className="grow rounded-xl overflow-hidden relative">
                  <Suspense fallback={<Spinner kind="inset" />}>
                    <InlineDocumentPreview
                      document={{
                        url: selectedDocument.sasUri,
                        extension: selectedDocument.extension,
                      }}
                    />
                  </Suspense>
                </div>
              </div>
            </>
          ) : (
            <div className="p-4 max-md:pb-0">
              <Card className="grow p-12 text-center text-muted">
                {tPage('details.selectDocument')}
              </Card>
            </div>
          )}
        </div>
      </MainScrollArea>
      <KbDocumentsUploadDialog
        knowledgeBaseId={knowledgeBaseId}
        knowledgeBaseName={knowledgeBase?.name}
        open={isUploadDialogOpen}
        onOpenChange={setIsUploadDialogOpen}
      />
    </>
  );
}
